import { type FC, useCallback } from 'react';

import { Checkbox } from '@vakantiesnl/components/src/atoms/Checkbox';
import { Radio } from '@vakantiesnl/components/src/atoms/Radio';

import { SelectBar, type SelectBarProps } from '../SelectBar';

export type BaseSingleSelectProps = Omit<SelectBarProps, 'selector'> & {
	/** Callback function with the value */
	onSelect?: (value: string | number, selected?: boolean) => void;
	/** Value passed on (de)select */
	value?: string | number;
	/** Label to create groups for radios */
	name?: string;
	/** The type of the input */
	variant?: 'radio' | 'checkbox';
};

export const BaseSingleSelect: FC<BaseSingleSelectProps> = ({
	id,
	selected,
	onSelect,
	tag: tagColumn,
	value,
	name,
	variant = 'radio',
	isDisabled,
	className,
	dataCy = 'listSelectOption',
	content,
}) => {
	const handleCheckBoxOnSelect = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const checked = event.target.checked;
			if (value !== undefined) {
				onSelect?.(value, checked);
			}
		},
		[onSelect, value],
	);

	const handleOnSelect = useCallback((): void => {
		if (value !== undefined) {
			onSelect?.(value);
		}
	}, [onSelect, value]);

	return (
		<SelectBar
			className={className}
			dataCy={dataCy}
			tag={tagColumn}
			id={id}
			selected={selected}
			content={content}
			selector={
				variant === 'checkbox' ? (
					<Checkbox checked={selected} disabled={isDisabled} onChange={handleCheckBoxOnSelect} id={id} />
				) : (
					<Radio
						hasNoFocusState={true}
						disabled={isDisabled}
						name={name}
						id={id}
						checked={selected}
						onChange={handleOnSelect}
					/>
				)
			}
		/>
	);
};
